import type {
    ClassicalAPIResponse,
    ClassicalSearchListItem,
} from '@jet-app/music-classical/api/types';
import { fixArtwork, type PageHeader } from '@jet-app/music-classical/models';
import type {
    SearchSuggestions,
    SearchSuggestionLockup,
} from '@jet-app/music-classical/models/pages/search';
import { createFlowActionForItem } from '@jet-app/music-classical/utilities/create-flow-action';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';

export function renderSearchSuggestions(
    apiResponse: ClassicalAPIResponse,
    objectGraph: ObjectGraph,
): SearchSuggestions {
    const sections = apiResponse?.body?.sections?.filter(
        (section: any) => section.type !== 'see-all',
    );
    const items = extractItemsBasedOnLimits(sections);

    // Transform from Classical API List Item to Search Suggestion
    const suggestionItems = items
        .map((item: ClassicalSearchListItem) =>
            transformToSearchSuggestion(item, objectGraph),
        )
        .filter(Boolean);

    return {
        pageKind: 'componentScreen',
        kind: 'SearchSuggestions',
        header: {} as unknown as PageHeader,
        sections: [],
        items: suggestionItems,
    };
}

function transformToSearchSuggestion(
    item: ClassicalSearchListItem,
    objectGraph: ObjectGraph,
): SearchSuggestionLockup {
    const segue = createFlowActionForItem(item, objectGraph) || null;
    const localization = inject(ObjectGraphType.localization, objectGraph);
    const baseAttributes = {
        type: item.type,
        segue,
        artwork: fixArtwork(item?.image),
        displayTerm: item?.title,
        title: item?.title,
        subtitle: item?.subtitle,
    };

    switch (item.type) {
        case 'playlist':
            return {
                ...baseAttributes,
                subtitle: localization.string(
                    'ASE.Web.MusicClassicalClientJS.ContentA.Middot.ContentB',
                    {
                        contentA: localization.string(
                            'ASE.Web.MusicClassicalClientJS.Playlist',
                        ),
                        contentB: item?.description,
                    },
                ),
            };
        case 'album':
            return {
                ...baseAttributes,
                subtitle: localization.string(
                    'ASE.Web.MusicClassicalClientJS.ContentA.Middot.ContentB',
                    {
                        contentA: localization.string(
                            'ASE.Web.MusicClassicalClientJS.Album',
                        ),
                        contentB: item?.artist,
                    },
                ),
            };
        case 'work':
            return {
                ...baseAttributes,
                subtitle: item?.opusAndAltTitle,
                headline: item?.composerName,
            };
        case 'track':
            return {
                ...baseAttributes,
                subtitle: localization.string(
                    'ASE.Web.MusicClassicalClientJS.ContentA.Middot.ContentB',
                    {
                        contentA: localization.string(
                            'ASE.Web.MusicClassicalClientJS.Track',
                        ),
                        contentB: item?.subtitle,
                    },
                ),
            };
        case 'subheading':
            return null;
        default:
            return baseAttributes;
    }
}

/**
 * Given a list of sections with items, take an item from each section
 * to build a list of up to 12 total items. Some restrictions:
 *   - Take no more than 5 items per section
 *   - Preserve item order by section
 *
 * Spec: https://quip-apple.com/aJx8AxZVtGsb
 */
const SEARCH_HINTS_TOTAL_MAX_COUNT = 12;
const SEARCH_HINTS_MAX_COUNT_PER_SECTION = 5;
function extractItemsBasedOnLimits(
    sections: Array<unknown>,
): ClassicalSearchListItem[] {
    // Keeps track of items by section type so we know how many of each type there are
    //      e.g. [{ 'instruments': [...] }, { 'albums': [...] }]
    const itemsByType = new Map<string, any[]>();

    // Keeps track of how many total items we have
    let itemCount: number = 0;

    // Keeps track of the index of the item in each section. We look at the
    // first item of each section, then second item of each section, etc.
    let currentIndex = 0;

    // If we don't yet have up to 12 results, try adding items from sections
    while (itemCount < SEARCH_HINTS_TOTAL_MAX_COUNT) {
        // Keeps track of whether or not we were able to add an item
        let didAddItem: boolean = false;

        for (const section of sections) {
            let items = section.components?.[0]?.items;
            if (section.type === 'track-list') {
                items = items.filter((item) => item.type === 'track');
            }

            if (!itemsByType.get(section.type)) {
                itemsByType.set(section.type, []);
            }
            const typeItems = itemsByType.get(section.type);
            const item = items[currentIndex];
            // Check if section has an available item and we have not yet reached the max items per section
            if (item && typeItems.length < SEARCH_HINTS_MAX_COUNT_PER_SECTION) {
                itemsByType.set(section.type, [...typeItems, item]);
                itemCount++;
                didAddItem = true;
            }

            // If we've found 12 results, we can stop looking
            if (itemCount >= SEARCH_HINTS_TOTAL_MAX_COUNT) {
                break;
            }
        }

        // If we weren't able to add any items from any section, we should stop looking
        if (!didAddItem) {
            break;
        }

        currentIndex++;
    }

    return Array.from(itemsByType.values()).flat();
}
